import { fluidRange } from 'polished';
import { motion } from 'framer-motion';
import { Number } from 'react-credit-card-primitives';
import PropTypes from 'prop-types';
import { useState } from 'react';
import styled, { css } from '@happymoneyinc/styled-components';
import MaskedInput from '@happymoneyinc/react-text-mask';
import ThemeWrapper from '../utils/ThemeWrapper/index.js';
import { focus } from '../utils/global-styles.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import Paragraph from '../Paragraph/Paragraph.js';
import SvgIcon from '../SvgIcon/SvgIcon.js';

const NO_MASK = {
  guide: false,
  mask: false,
  placeholder: ''
};
const Input = props => {
  const {
    autoComplete,
    autoFocus,
    className,
    disabled,
    errorText,
    hintText,
    inputClassName,
    id,
    isInvalid: $isInvalid,
    label,
    maskProps,
    maxLength,
    onBlur,
    onChange,
    onCut,
    onCopy,
    onFocus,
    onPaste,
    placeholder,
    required,
    showHint,
    testID,
    type,
    validations,
    showValidIcon: $showValidIcon,
    validIconType,
    value,
    optional: showOptionalText,
    ...attributes
  } = props;
  const initialType = type;
  const [currentType, setCurrentType] = useState(type);
  const inputMasks = {
    email: {
      guide: false,
      mask: false,
      maxLength: 100,
      placeholder: 'name@website.com'
    },
    tel: {
      guide: true,
      mask: ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
      placeholder: '(___) ___-____'
    },
    ssn: {
      guide: false,
      mask: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
      placeholder: '___-__-____',
      type: 'password'
    },
    zip: {
      guide: false,
      mask: [/\d/, /\d/, /\d/, /\d/, /\d/]
    }
  };
  const mask = maskProps || inputMasks[type] || NO_MASK;
  return /*#__PURE__*/jsx(ThemeWrapper, {
    children: /*#__PURE__*/jsxs(InputContainer, {
      className: className,
      children: [showHint && /*#__PURE__*/jsx(HintContainer, {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        transition: {
          duration: 0.2,
          ease: 'easeOut'
        },
        exit: {
          opacity: 0
        },
        children: /*#__PURE__*/jsx(Paragraph, {
          size: "xs",
          children: hintText
        })
      }), /*#__PURE__*/jsxs(InputBody, {
        children: [/*#__PURE__*/jsxs(Top, {
          children: [/*#__PURE__*/jsx(StyledLabel, {
            htmlFor: id,
            children: label
          }), showOptionalText && /*#__PURE__*/jsx(OptionalText, {
            "data-testid": `optional-${testID}`,
            children: "Optional"
          })]
        }), type === 'currency' ? /*#__PURE__*/jsx(DollarSign, {
          children: "$"
        }) : '', type === 'card' ? /*#__PURE__*/jsx(Number, {
          onChange: onChange,
          masked: true,
          render: _ref => {
            let {
              getInputProps
            } = _ref;
            return /*#__PURE__*/jsx(StyledCCInput, {
              ...getInputProps(),
              className: `${inputClassName} ho-input`,
              "data-testid": `${testID}-${initialType}`,
              disabled: disabled,
              placeholder: placeholder,
              required: required,
              maxLength: maxLength
            });
          }
        }) : /*#__PURE__*/jsx(StyledInput, {
          autoComplete: autoComplete,
          autoFocus: autoFocus,
          className: `${inputClassName} ho-input`,
          "data-testid": `${testID}-${initialType}`,
          mask: mask.mask,
          onBlur: onBlur,
          onChange: onChange,
          onCopy: onCopy,
          onCut: onCut,
          onFocus: onFocus,
          onPaste: onPaste,
          disabled: disabled,
          id: id,
          $isInvalid: $isInvalid,
          $showValidIcon: $showValidIcon,
          placeholder: placeholder || mask.placeholder,
          type: mask.type || currentType,
          maxLength: maxLength,
          required: required,
          value: value,
          guide: mask.guide,
          ...attributes
        }), type !== 'password' ? /*#__PURE__*/jsx(ValidIcon, {
          type: validIconType,
          className: "ho-valid-checkmark ho-valid-icon",
          testID: "valid-icon"
        }) : null, initialType === 'password' && /*#__PURE__*/jsx(VisiblityToggle, {
          onClick: () => {
            setCurrentType(currentType === 'password' ? 'text' : 'password');
          },
          onKeyPress: e => e.keyCode === 13 ? setCurrentType(currentType === 'password' ? 'text' : 'password') : null,
          type: "button",
          children: /*#__PURE__*/jsx(SvgIcon, {
            type: currentType === 'password' ? 'passwordActive' : 'passwordInactive'
          })
        })]
      }), $isInvalid && /*#__PURE__*/jsx(motion.div, {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        transition: {
          duration: 0.25,
          ease: 'easeOut'
        },
        exit: {
          opacity: 0
        },
        "data-testid": "error",
        children: /*#__PURE__*/jsx(ErrorText, {
          size: "sm",
          children: errorText
        })
      }), validations && /*#__PURE__*/jsx(Validations, {
        children: validations.map(validation => /*#__PURE__*/jsxs(Validation, {
          children: [/*#__PURE__*/jsx(Indicator, {
            isValid: validation.isValid
          }), /*#__PURE__*/jsx(ValidationText, {
            className: "ho-paragraph",
            children: validation.text
          })]
        }, validation.text))
      })]
    })
  });
};
const Indicator = styled.div`
  width: 10px;
  height: 10px;
  margin-right: 6px;
  background-color: ${_ref2 => {
  let {
    theme,
    isValid
  } = _ref2;
  return isValid ? theme.colors.systemGreen : theme.colors.gray[4];
}};
  border-radius: 50%;
`;
const InputContainer = styled.div`
  position: relative;
  width: 100%;

  @media (min-width: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.screen.lg;
}}) {
    height: auto;
    margin-bottom: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.spacing[4];
}};
  }
`;
const DollarSign = styled.span`
  position: absolute;
  bottom: 0;
  display: block;
  padding: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return `0 0 ${theme.spacing[4]} ${theme.spacing[4]}`;
}};
  font-family: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.fonts.MabryProRegular;
}};
  line-height: 1rem;
`;
const ValidIcon = styled(SvgIcon)`
  position: absolute;
  top: 36px;
  right: 20px;
`;
const Validations = styled.div`
  justify-content: space-evenly;
  display: flex;
`;
const Validation = styled.div`
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  display: flex;
  margin: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.spacing[3];
}} 0;
`;
const ValidationText = styled.p`
  margin: 0;
`;
const VisiblityToggle = styled.button`
  ${focus}

  position: absolute;
  top: 20px;
  right: 5px;
  padding: 0;
  appearance: none;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
`;
const StyledLabel = styled.label`
  color: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.colors.gray[2];
}};
  font-family: ${_ref9 => {
  let {
    theme
  } = _ref9;
  return theme.fontStyles.label.fontFamily;
}};
  font-size: ${_ref10 => {
  let {
    theme
  } = _ref10;
  return `${theme.fontStyles.label.fontSize}px`;
}};
`;
const baseInputStyles = css`
  ${_ref11 => {
  let {
    theme
  } = _ref11;
  return fluidRange({
    prop: 'font-size',
    fromSize: `${theme.fontStyles.paragraphs.lg.fontSizeMin}px`,
    toSize: `${theme.fontStyles.paragraphs.lg.fontSizeMax}px`
  }, `${theme.screen.xxs}`, `${theme.screen.xl}`);
}}

  width: 100%;
  height: 100%;
  min-height: 72px;
  ${_ref12 => {
  let {
    disabled,
    $isInvalid,
    theme
  } = _ref12;
  if ($isInvalid) {
    return `border: 2px solid ${theme.colors.systemRed};`;
  }
  if (disabled) {
    return 'border: 2px solid transparent;';
  }
  return `border: 2px solid ${theme.colors.gray[4]};`;
}}

  padding: ${_ref13 => {
  let {
    theme,
    type,
    $showValidIcon
  } = _ref13;
  let [pt, pr, pb, pl] = [theme.spacing[6], theme.spacing[6], theme.spacing[2], theme.spacing[4]];
  if (type === 'currency') {
    pl = '30px';
  }
  if ($showValidIcon) {
    pr = `calc(${pr} + 5px) `;
  }
  return [pt, pr, pb, pl].join(' ');
}};
  color: ${_ref14 => {
  let {
    disabled,
    theme
  } = _ref14;
  return disabled ? theme.colors.gray[3] : theme.colors.black;
}};
  font-family: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return theme.fonts.MabryProRegular;
}};
  line-height: 1.5;
  background: ${_ref16 => {
  let {
    disabled,
    theme
  } = _ref16;
  return disabled ? theme.colors.gray[5] : theme.colors.white;
}};
  border-radius: 3px;
  outline: none;
  transition: border 250ms;
  cursor: ${_ref17 => {
  let {
    disabled
  } = _ref17;
  return disabled ? 'not-allowed' : 'auto';
}};

  + ${ValidIcon} {
    display: ${_ref18 => {
  let {
    disabled,
    $showValidIcon
  } = _ref18;
  if ($showValidIcon) {
    return 'block';
  }
  return 'none';
}};
  }

  :hover {
    ${_ref19 => {
  let {
    disabled,
    $isInvalid,
    theme
  } = _ref19;
  if ($isInvalid) {
    return `border: 2px solid ${theme.colors.systemRed};`;
  }
  if (disabled) {
    return 'border: 2px solid transparent;';
  }
  return `border: 2px solid ${theme.colors.black};`;
}}
  }

  ::placeholder {
    color: ${_ref20 => {
  let {
    theme
  } = _ref20;
  return theme.colors.gray[3];
}};
  }

  :focus {
    border: ${_ref21 => {
  let {
    theme,
    $isInvalid
  } = _ref21;
  return $isInvalid ? `2px solid ${theme.colors.systemRed}` : `2px solid ${theme.colors.black}`;
}};
  }
`;
const StyledInput = styled(MaskedInput)`
  ${baseInputStyles}
`;
const StyledCCInput = styled.input`
  ${baseInputStyles}
`;
const HintContainer = styled(motion.div)`
  position: relative;
  top: 0;
  right: 0;
  left: 0;
  width: 100%;
  padding: ${_ref22 => {
  let {
    theme
  } = _ref22;
  return theme.spacing[4];
}};
  margin-bottom: ${_ref23 => {
  let {
    theme
  } = _ref23;
  return theme.spacing[5];
}};
  background: ${_ref24 => {
  let {
    theme
  } = _ref24;
  return theme.colors.flamingo[3];
}};
  border-radius: 16px;
  transform: none;

  @media (min-width: ${_ref25 => {
  let {
    theme
  } = _ref25;
  return theme.screen.lg;
}}) {
    position: absolute;
    right: ${_ref26 => {
  let {
    theme
  } = _ref26;
  return `-${theme.spacing[6]}`;
}};
    left: auto;
    width: 100%;
    max-width: 238px;
    transform: translateX(100%);
  }

  &::after {
    position: absolute;
    bottom: -7px;
    left: 11px;
    display: block;
    width: 0;
    height: 0;
    content: '';
    border-right: 15px solid transparent;
    border-bottom: ${_ref27 => {
  let {
    theme
  } = _ref27;
  return `15px solid ${theme.colors.flamingo[3]}`;
}};
    border-left: 15px solid transparent;
    transform: rotate(85deg);

    @media (min-width: ${_ref28 => {
  let {
    theme
  } = _ref28;
  return theme.screen.lg;
}}) {
      top: 12px;
      left: -19px;
      transform: rotate(45deg);
    }
  }
`;
const ErrorText = styled(Paragraph)`
  margin-top: ${_ref29 => {
  let {
    theme
  } = _ref29;
  return theme.spacing[3];
}};
  color: ${_ref30 => {
  let {
    theme
  } = _ref30;
  return theme.colors.systemRed;
}};
  text-align: center;
`;
const InputBody = styled.div`
  position: relative;
`;
const Top = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: space-between;
  display: flex;
  width: 100%;
  padding: 9px 18px;
  pointer-events: none;

`;
const topStyles = css`
  color: ${_ref31 => {
  let {
    theme
  } = _ref31;
  return theme.colors.gray[2];
}};
  font-family: ${_ref32 => {
  let {
    theme
  } = _ref32;
  return theme.fontStyles.label.fontFamily;
}};
`;
const OptionalText = styled.span`
  ${topStyles}

  font-size: ${_ref33 => {
  let {
    theme
  } = _ref33;
  return `${theme.fontStyles.paragraphs.xxs.fontSizeMin}px`;
}};
`;
Input.defaultProps = {
  autoComplete: 'on',
  autoFocus: false,
  className: '',
  disabled: false,
  errorText: '',
  hintText: '',
  id: '',
  inputClassName: '',
  isInvalid: null,
  label: '',
  maskProps: null,
  maxLength: 70,
  onBlur: null,
  onChange: () => null,
  onCopy: null,
  onCut: null,
  onFocus: null,
  onPaste: null,
  placeholder: '',
  required: false,
  showHint: false,
  testID: 'input',
  type: 'text',
  showValidIcon: false,
  validIconType: 'circleCheck',
  validations: null,
  value: '',
  optional: null
};
Input.propTypes = {
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  errorText: PropTypes.node,
  hintText: PropTypes.node,
  id: PropTypes.string,
  inputClassName: PropTypes.string,
  isInvalid: PropTypes.bool,
  label: PropTypes.string,
  maskProps: PropTypes.shape({
    guide: PropTypes.bool,
    mask: PropTypes.oneOfType([PropTypes.array, PropTypes.func, PropTypes.bool, PropTypes.shape({
      mask: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
      pipe: PropTypes.func
    })])
  }),
  maxLength: PropTypes.number,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onCopy: PropTypes.func,
  onCut: PropTypes.func,
  onPaste: PropTypes.func,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  showHint: PropTypes.bool,
  testID: PropTypes.string,
  type: PropTypes.oneOf(['text', 'email', 'tel', 'ssn', 'zip', 'password', 'card', 'currency']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showValidIcon: PropTypes.bool,
  validIconType: PropTypes.string,
  optional: PropTypes.bool,
  validations: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    isValid: PropTypes.bool
  }))
};
var Input$1 = Input;

export { Input$1 as default };
//# sourceMappingURL=Input.js.map
